import { IconTools } from '@tabler/icons-react-native';
import { Perishable } from 'app/design/icons/perishable';
import { Column, Row } from 'app/design/primitives/box';
import { ControlledInput } from 'app/design/primitives/input/controlled';
import { MultiImagePicker } from 'app/design/primitives/multi-image-picker';
import { Text } from 'app/design/primitives/text';
import { useFormContext } from 'app/hooks/form';
import { MAX_PICTURES } from 'app/hooks/use-imagepicker-camera';
import { useUser } from 'app/network/user';
import { LoanFeeFormFields, LoanFeeModals, useLoanFeeModals } from 'app/screens/management/loan-fee';
import { AvailabilitySection, InventoryField } from 'app/screens/shared/one-listing/components/availability-section';
import { createTheme as createDSTheme } from 'app/shopify';
import type { CreateListingDTO } from 'app/types/listing';
import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useUISize } from 'app/utils/web-helpers';
import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect, useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';
import type { ListingsVisibilityExclusionsPreference } from '../index';
import { DuplicatesStepper } from './duplicates-stepper';
import { ExpirableAvailabilitySection } from './expirable-availability-section';
import { GroupsSection } from './groups-section';
import { TransactionModeToggle } from './transaction-mode-toggle';
import { UrgentSection } from './urgent-section';

const theme = createDSTheme();

export function ResourceTypeButton({
  icon,
  label,
  onPress,
  selected,
}: {
  icon: React.ReactNode;
  label: string;
  onPress: () => void;
  selected: boolean;
}) {
  const { isLargeUI } = useUISize();

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => ({
        backgroundColor: selected ? theme.colors['surface.inverse.default'] : 'transparent',
        borderRadius: theme.borderRadii['radius.lg'],
        flex: 1,
        minWidth: 0,
        opacity: pressed ? 0.8 : 1,
        overflow: 'hidden',
      })}
    >
      <Row
        alignItems="center"
        justifyContent="center"
        gap="spacing.general.x2"
        minHeight={72}
        paddingHorizontal="spacing.general.x2"
      >
        <Column flexShrink={0}>{icon}</Column>
        <Text
          color={selected ? 'text.inverse.primary' : 'text.primary'}
          flex={isLargeUI ? undefined : 1}
          fontWeight={selected ? '700' : '500'}
          minWidth={0}
          textAlign="center"
          variant="bodyBase"
        >
          {label}
        </Text>
      </Row>
    </Pressable>
  );
}

export function HauteGaspesieOfferForm({
  form,
  setListingsVisibilityExclusionsPreference,
}: {
  form: UseFormReturn<CreateListingDTO>;
  setListingsVisibilityExclusionsPreference?: Dispatch<SetStateAction<ListingsVisibilityExclusionsPreference>>;
}) {
  const { t } = useTranslation('new');
  const { control, setValue, watch } = useFormContext<CreateListingDTO>();
  const { data: user } = useUser();
  const perishableEnabled = isFeatureEnabled(FeatureFlag.perishableListings, user?.featureFlags);
  const subtype = watch('subtype');
  const transactionMode = watch('transactionMode');
  const isExpirable = subtype === ListingSubtype.expirable;
  const [loanFeeEnabled, setLoanFeeEnabled] = useState(false);
  const loanFeeModalsState = useLoanFeeModals();

  useEffect(() => {
    if (!transactionMode || (isExpirable && transactionMode === TransactionMode.lend)) {
      setValue('transactionMode', TransactionMode.sell, { shouldValidate: true });
    }
  }, [isExpirable, setValue, transactionMode]);

  const setResourceType = (nextSubtype: ListingSubtype.object | ListingSubtype.expirable) => {
    setValue('subtype', nextSubtype, { shouldDirty: true, shouldValidate: true });
    if (nextSubtype === ListingSubtype.expirable && transactionMode === TransactionMode.lend) {
      setValue('transactionMode', TransactionMode.sell, { shouldDirty: true, shouldValidate: true });
    }
    if (nextSubtype === ListingSubtype.object) {
      setValue('start', null, { shouldDirty: true, shouldValidate: true });
      setValue('end', null, { shouldDirty: true, shouldValidate: true });
      setValue('urgent', false, { shouldDirty: true, shouldValidate: true });
    }
  };

  const setMode = (mode: TransactionMode) => {
    setValue('transactionMode', mode, { shouldDirty: true, shouldValidate: true });
    if (mode !== TransactionMode.sell) {
      setValue('price', null, { shouldDirty: true, shouldValidate: true });
    }
    if (mode !== TransactionMode.lend) {
      setLoanFeeEnabled(false);
      setValue('loanFee', null);
      setValue('loanFeeDescription', null);
      setValue('loanFeePaymentMethod', null);
    }
  };

  const transactionOptions = [
    { label: t('hauteGaspesie.transaction.sell'), value: TransactionMode.sell },
    ...(!isExpirable ? [{ label: t('hauteGaspesie.transaction.lend'), value: TransactionMode.lend }] : []),
    { label: t('hauteGaspesie.transaction.give'), value: TransactionMode.give },
  ];

  return (
    <Column alignSelf="center" gap="spacing.general.x4" maxWidth={600} width="100%">
      <Row
        backgroundColor="surface.primary-neutral"
        borderRadius="radius.xl"
        gap="spacing.general.x1"
        padding="spacing.general.x1"
      >
        <ResourceTypeButton
          icon={
            <IconTools
              strokeWidth={ICON_STROKE_WIDTH}
              size={22}
              color={!isExpirable ? theme.colors['text.inverse.primary'] : theme.colors['text.primary']}
            />
          }
          label={t('hauteGaspesie.resourceType.equipment')}
          onPress={() => setResourceType(ListingSubtype.object)}
          selected={!isExpirable}
        />
        {perishableEnabled ? (
          <ResourceTypeButton
            icon={
              <Perishable
                strokeWidth={ICON_STROKE_WIDTH}
                width={40}
                height={40}
                color={isExpirable ? theme.colors['text.inverse.primary'] : theme.colors['text.primary']}
              />
            }
            label={t('hauteGaspesie.resourceType.perishable')}
            onPress={() => setResourceType(ListingSubtype.expirable)}
            selected={isExpirable}
          />
        ) : null}
      </Row>

      <Controller
        control={control}
        name="pictures"
        render={({ field, fieldState }) => (
          <MultiImagePicker
            emptyHint={t('hauteGaspesie.pictureHint', { max: MAX_PICTURES })}
            error={fieldState.error}
            images={field.value ?? []}
            maxImages={MAX_PICTURES}
            onChange={field.onChange}
            title={t('hauteGaspesie.addPicture')}
          />
        )}
      />

      <ControlledInput
        control={control}
        label={t('hauteGaspesie.resourceName')}
        labelSuffix={`(${t('mandatory', { ns: 'common' })})*`}
        maxLength={64}
        name="item.name"
        placeholder={t('hauteGaspesie.resourceNamePlaceholder')}
        testID="new-listing-title-input"
      />

      <ControlledInput
        control={control}
        label={t('itemInformationSection.descriptionFieldLabel')}
        labelSuffix={`(${t('suggestede', { ns: 'common' })})`}
        maxLength={280}
        multiline
        name="description"
        nullable
        placeholder={t('hauteGaspesie.descriptionPlaceholder')}
        scrollEnabled={false}
        style={{ minHeight: 96 }}
      />

      <Column
        backgroundColor="surface.tertiary-neutral"
        borderRadius="radius.xl"
        gap="spacing.general.x4"
        padding="spacing.general.x4"
      >
        <TransactionModeToggle onChange={setMode} options={transactionOptions} value={transactionMode} />

        {transactionMode === TransactionMode.sell ? (
          <ControlledInput
            backgroundColor={theme.colors['surface.default']}
            control={control}
            label={t('hauteGaspesie.price')}
            labelSuffix={`(${t('mandatory', { ns: 'common' })})*`}
            name="price"
            placeholder={t('hauteGaspesie.pricePlaceholder')}
          />
        ) : null}

        {transactionMode === TransactionMode.lend ? (
          <LoanFeeFormFields
            loanFeeEnabled={loanFeeEnabled}
            modalsState={loanFeeModalsState}
            setLoanFeeEnabled={setLoanFeeEnabled}
          />
        ) : null}

        {isExpirable ? <ExpirableAvailabilitySection /> : null}

        {isExpirable ? <InventoryField form={form} /> : null}

        {isExpirable ? <UrgentSection /> : null}

        {!isExpirable ? <AvailabilitySection form={form} useInventory /> : null}
      </Column>

      <GroupsSection
        alwaysShow
        includeAllSectorGroups
        setListingsVisibilityExclusionsPreference={setListingsVisibilityExclusionsPreference}
      />
      <DuplicatesStepper form={form} />
      <LoanFeeModals state={loanFeeModalsState} />
    </Column>
  );
}
